import React, { useEffect, useState } from 'react';
import api from '../utils/api';

/**
 * Admin Diagnostics — read-only health checks over the database.
 *
 * Currently exposes the orphan-reference scan: walks every collection and
 * lists references that point to entities that no longer exist (deleted
 * customers, products, users, etc.). Useful right after a customer was
 * deleted to confirm nothing got stranded.
 */
export default function AdminDiagnostics() {
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [err, setErr] = useState('');
  const [filter, setFilter] = useState('with_orphans'); // 'with_orphans' | 'all'

  const runScan = async () => {
    try {
      setLoading(true);
      setErr('');
      const res = await api.get('/admin/diagnostics/orphan-scan');
      setReport(res.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Failed to run scan');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { runScan(); /* run on mount */ }, []); // eslint-disable-line

  const totals = report?.totals;
  const results = report?.results || [];
  const visible = filter === 'with_orphans'
    ? results.filter((r) => r.orphan_count > 0)
    : results;
  const withOrphans = results.filter((r) => r.orphan_count > 0);
  const downloadJson = () => {
    if (!report) return;
    const blob = new Blob([JSON.stringify(report, null, 2)],
      { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `orphan_scan_${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="admin-diagnostics-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-900">🔧 Diagnostics</h1>
          <p className="text-sm text-gray-600 mt-1">
            Read-only database integrity checks. Run anytime to find dangling
            references to deleted customers, products, users, etc.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={runScan}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-4 py-2 rounded-lg shadow disabled:opacity-50"
            data-testid="run-orphan-scan-btn"
          >
            {loading ? 'Scanning…' : '↻ Re-run Scan'}
          </button>
          <button
            onClick={downloadJson}
            disabled={!report}
            className="bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold px-3 py-2 rounded-lg disabled:opacity-50"
            data-testid="download-orphan-report"
          >
            ⬇ JSON
          </button>
        </div>
      </div>

      {err && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded p-3">
          {err}
        </div>
      )}

      {loading && !report && (
        <div className="bg-blue-50 border border-blue-100 text-blue-700 text-sm rounded p-4">
          Running scan…
        </div>
      )}

      {/* Summary cards */}
      {totals && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <SummaryCard
            label="Orphan references"
            value={totals.total_orphan_references}
            color={totals.total_orphan_references > 0 ? 'red' : 'green'}
            testid="diag-total-orphans"
          />
          <SummaryCard
            label="Collections w/ orphans"
            value={totals.collections_with_orphans}
            color={totals.collections_with_orphans > 0 ? 'amber' : 'green'}
          />
          <SummaryCard
            label="Clean checks"
            value={totals.clean_checks}
            color="green"
          />
          <SummaryCard
            label="Records scanned"
            value={Object.values(totals.ref_sets || {}).reduce((a, b) => a + b, 0)}
            color="gray"
            hint="across all parent collections"
          />
        </div>
      )}

      {/* Filter pills */}
      {report && (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-600">Show:</span>
          <Pill
            active={filter === 'with_orphans'}
            onClick={() => setFilter('with_orphans')}
            testid="filter-with-orphans"
          >
            With orphans only ({withOrphans.length})
          </Pill>
          <Pill
            active={filter === 'all'}
            onClick={() => setFilter('all')}
            testid="filter-all"
          >
            All checks ({results.length})
          </Pill>
          {report.started_at && (
            <span className="ml-auto text-xs text-gray-400">
              Last scan: {new Date(report.started_at).toLocaleString()}
            </span>
          )}
        </div>
      )}

      {/* Results */}
      {report && visible.length === 0 && filter === 'with_orphans' && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg p-6 text-center">
          <div className="text-4xl mb-1">✅</div>
          <div className="font-bold text-lg">All clean</div>
          <div className="text-sm text-emerald-700 mt-1">
            No orphan references found anywhere in the database.
          </div>
        </div>
      )}

      <div className="space-y-3">
        {visible.map((r, i) => (
          <ResultCard key={`${r.collection}-${r.field}-${i}`} r={r} />
        ))}
      </div>
    </div>
  );
}

function SummaryCard({ label, value, color, hint, testid }) {
  const palette = {
    red: 'bg-red-50 border-red-200 text-red-900',
    amber: 'bg-amber-50 border-amber-200 text-amber-900',
    green: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    gray: 'bg-gray-50 border-gray-200 text-gray-900',
  };
  return (
    <div className={`border rounded-lg p-3 ${palette[color] || palette.gray}`}
         data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider font-semibold opacity-70">{label}</div>
      <div className="text-2xl font-extrabold mt-1">{value}</div>
      {hint && <div className="text-[10px] opacity-60 mt-0.5">{hint}</div>}
    </div>
  );
}

function Pill({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`text-xs px-3 py-1 rounded-full font-semibold transition ${
        active
          ? 'bg-blue-600 text-white shadow'
          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
      }`}
    >
      {children}
    </button>
  );
}

// Returns a short, actionable instruction for fixing a particular orphan
// category. Keyed primarily off `ref_type`; falls back to generic guidance.
function _fixHint(collection, refType, field) {
  if (refType === 'customer') {
    if (field?.includes('serial_numbers'))
      return 'Edit the product, find this serial, and re-assign or clear the customer.';
    return 'Open the record and select an existing customer (or delete if obsolete).';
  }
  if (refType === 'user') {
    if (field === 'sales_rep_id') return 'Open the invoice and select an active Sales Rep.';
    if (field === 'assigned_to')  return 'Open the record and assign an active user.';
    if (field === 'created_by')   return 'Open the record (admin) and edit "Created By" to an active user.';
    return 'Open the record and re-assign or clear the user field.';
  }
  if (refType === 'product')  return 'Open the document, remove the deleted line item, and re-select an active product.';
  if (refType === 'supplier') return 'Open the document and select an existing supplier.';
  if (refType === 'team')     return 'Open the user profile and assign to an existing team.';
  if (refType === 'lead')     return 'Open the activity and clear or re-assign the linked lead.';
  if (refType === 'company')  return 'Contact admin — company-id remaps are sensitive.';
  return 'Open the record and re-link to an existing entity.';
}


function ResultCard({ r }) {
  const isClean = r.orphan_count === 0;
  return (
    <div
      className={`border rounded-lg overflow-hidden ${
        isClean ? 'border-emerald-200' : 'border-amber-300'
      }`}
    >
      <div
        className={`px-4 py-2 flex items-center justify-between ${
          isClean ? 'bg-emerald-50' : 'bg-amber-50'
        }`}
      >
        <div className="flex items-center gap-2 text-sm">
          <span className={`font-bold ${isClean ? 'text-emerald-700' : 'text-amber-800'}`}>
            {isClean ? '✓' : '⚠'} {r.collection}
          </span>
          <span className="text-gray-500">→</span>
          <span className="font-mono text-xs text-gray-700">{r.field}</span>
          {r.ref_type && (
            <span className="text-[10px] uppercase font-semibold bg-white border rounded px-1.5 py-0.5 text-gray-500">
              {r.ref_type}
            </span>
          )}
        </div>
        <div className={`text-sm font-extrabold ${isClean ? 'text-emerald-700' : 'text-red-700'}`}>
          {r.orphan_count} orphan{r.orphan_count === 1 ? '' : 's'}
        </div>
      </div>
      {!isClean && r.samples?.length > 0 && (
        <div className="bg-white px-4 py-3 text-xs">
          <div className="text-gray-500 mb-2">
            Showing first {Math.min(r.samples.length, 10)} of {r.orphan_count} affected record(s):
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-1 pr-2">Record</th>
                  <th className="py-1 pr-2">Missing reference</th>
                  <th className="py-1 pr-2">How to fix</th>
                  <th className="py-1 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {r.samples.slice(0, 10).map((s, i) => (
                  <tr key={i} className="border-b last:border-0 align-top">
                    <td className="py-1.5 pr-2 font-mono text-gray-700">
                      <div>{s.display}</div>
                      {s.element_hint && (
                        <div className="text-gray-400 text-[10px] mt-0.5">↳ {s.element_hint}</div>
                      )}
                      {s.record_id && (
                        <div className="text-gray-400 text-[10px] mt-0.5">id: {s.record_id}</div>
                      )}
                    </td>
                    <td className="py-1.5 pr-2 text-gray-600">
                      <div className="font-semibold text-amber-700">
                        {(s.missing_field || '').split('(field:')[0].trim() || 'Linked record was deleted'}
                      </div>
                      <div className="font-mono text-[10px] text-gray-400 mt-0.5">
                        broken ref → {s.ref_id_value}
                      </div>
                    </td>
                    <td className="py-1.5 pr-2 text-gray-600">
                      {_fixHint(r.collection, r.ref_type, r.field)}
                    </td>
                    <td className="py-1.5 text-right">
                      {s.fix_url ? (
                        <a
                          href={s.fix_url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-block bg-blue-50 hover:bg-blue-100 text-blue-700 font-semibold rounded px-2 py-0.5 border border-blue-200"
                        >
                          Open ↗
                        </a>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {r.orphan_count > 10 && (
            <div className="text-gray-400 mt-2 italic">
              … and {r.orphan_count - 10} more (download JSON for the full list).
            </div>
          )}
        </div>
      )}
    </div>
  );
}
